import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Button, Grid, Paper, TableContainer, Tooltip } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';
import * as api from '../../api/app/opcClientApi';
import { AlertDialog } from '../../components/core/AlertDialog';
import { ApiError } from '../../components/core/ApiError/ApiError';
import DataGridTable from '../../components/core/DataGridTable/DataGridTable';
import { Loader } from '../../components/core/Loader/Loader';
import { useTranslation } from '../../contexts/translation';
import { useDeleteDialog } from '../../hooks/useDeleteDialog';
import { OpcClient } from '../../types/app/opcClient.types';
import { dictionary } from '../../utils/core/dictionary';
import { paths } from '../../utils/core/routes';
interface Props {
  isReadOnly: boolean;
}
export function OpcClientList({ isReadOnly }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const {
    isOpen: isDeleteDialogOpen,
    idToDelete,
    setDeleteAttempt,
    cancelDeleteAttempt,
    confirmDeleteAttempt,
  } = useDeleteDialog();

  const { data, isLoading, isError } = useQuery<OpcClient[]>('opcClient', api.getOpcClientList);
  const { mutate: mutateDeleteOpcClient } = useMutation(api.deleteOpcClient);

  const columns = [
    { headerName: dictionary.INSTANCE_NAME, field: 'instanceName', minWidth: 250 },
    { headerName: dictionary.ENDPOINT_ADDRESS, field: 'endpointAddress', minWidth: 300 },
    { headerName: dictionary.COMMENT, field: 'comment', minWidth: 250 },
    {
      field: '',
      sortable: false,
      disableColumnMenu: true,
      hide: isReadOnly,
      minWidth: 150,
      flex: 1,
      align: 'right',
      renderCell: (params: any) => {
        return (
          <>
            {!isReadOnly && (
              <>
                <Tooltip title={t('general.action-buttons.edit')}>
                  <Button onClick={() => handleOnEdit(params.id)}>
                    <EditIcon />
                  </Button>
                </Tooltip>
                <Tooltip title={t('general.action-buttons.delete')}>
                  <Button onClick={() => handleOnDelete(params.id)}>
                    <DeleteIcon />
                  </Button>
                </Tooltip>
              </>
            )}
          </>
        );
      },
    },
  ];

  const handleOnCreate = () => navigate(paths.OPCCLIENT_CREATE);
  const handleOnEdit = (id: number) => navigate(`${paths.OPCCLIENT_DETAIL}/${id}`);
  const handleOnDelete = (id: number) => setDeleteAttempt(id);

  const handleConfirm = () => {
    if (!idToDelete) return;

    mutateDeleteOpcClient(idToDelete, {
      onSuccess: () => {
        enqueueSnackbar(t('page.opc-client.item-delete'), { variant: 'success' });
        queryClient.invalidateQueries(['opcClient']);
        confirmDeleteAttempt();
      },
      onError: (result: any) => {
        enqueueSnackbar(`${result}`, { variant: 'error' });
      },
    });
  };

  if (isError) return <ApiError />;
  if (isLoading || !data) return <Loader />;
  return (
    <>
      <Grid item xs={8}>
        <TableContainer component={Paper}>
          <div style={{ height: '70vh', width: '100%' }}>
            <DataGridTable
              height={'70vh'}
              data={data}
              columns={columns}
              fulltextSearch={true}
              autoPageSize={true}
            />
          </div>
          {!isReadOnly && (
            <Tooltip title={t('general.action-buttons.new')}>
              <Button onClick={handleOnCreate}>
                <AddIcon />
              </Button>
            </Tooltip>
          )}
        </TableContainer>
      </Grid>
      <AlertDialog
        open={isDeleteDialogOpen}
        title={dictionary.ITEM_DELETED}
        content={t('general.messages.warning.delete-message')}
        handleClose={cancelDeleteAttempt}
        handleConfirm={handleConfirm}
      />
    </>
  );
}
